import React, { useMemo, useState } from 'react';
import OperationStatus from './OperationStatus';
import { ClientConsolidationAPI } from '../services/clientConsolidation';
import { CustomersAPI } from '../services/customers';
import { FormaPagamentoAPI } from '../services/formaPagamento';
import { IntegrationsAPI } from '../services/integrations';
import { ProductConsolidationAPI } from '../services/productConsolidation';
import { ServiceCostCentersAPI } from '../services/serviceCostCenters';
import { getErrorMessage, isUnauthorizedError } from '../services/http';

function formatDateTime(value) {
  if (!value) return 'Nunca';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Nunca';
  return date.toLocaleString('pt-BR');
}

function compactResult(payload = {}) {
  const candidates = [
    payload.total && `${payload.total} lidos`,
    payload.inserted && `${payload.inserted} criados`,
    payload.updated && `${payload.updated} atualizados`,
    payload.customersUpdated && `${payload.customersUpdated} clientes normalizados`,
    payload.linksUpdated && `${payload.linksUpdated} vínculos normalizados`,
    payload.masterClientsUpdated && `${payload.masterClientsUpdated} clientes mestre normalizados`,
    payload.formasPagamentoSincronizadas && `${payload.formasPagamentoSincronizadas} formas sincronizadas`,
    payload.formasPagamentoInseridas && `${payload.formasPagamentoInseridas} formas inseridas`,
    payload.pedidosSincronizados && `${payload.pedidosSincronizados} pedidos sincronizados`,
    payload.pedidosInseridos && `${payload.pedidosInseridos} pedidos inseridos`,
    payload.byCollection?.clientes_integrados && `${payload.byCollection.clientes_integrados} clientes integrados`,
    payload.byCollection?.produtos_integrados && `${payload.byCollection.produtos_integrados} produtos integrados`,
    payload.byCollection?.contatobling && `${payload.byCollection.contatobling} contatos Bling`,
    payload.byCollection?.produtobling && `${payload.byCollection.produtobling} produtos Bling`,
    payload.byCollection?.auvo_clientes && `${payload.byCollection.auvo_clientes} clientes Auvo`,
    payload.byCollection?.auvo_produtos && `${payload.byCollection.auvo_produtos} produtos Auvo`,
  ].filter(Boolean);

  return candidates.length ? candidates.join(' · ') : 'Concluído';
}

function resultTone(status) {
  if (status === 'error') return 'danger';
  if (status === 'success') return 'success';
  return 'secondary';
}

export default function SyncCenterPanel({ pageMode = false, onClose, onUnauthorized }) {
  const [activeAction, setActiveAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [results, setResults] = useState({});
  const [customerParams, setCustomerParams] = useState({ limit: 300, normalizeLimit: 100 });
  const [productParams, setProductParams] = useState({ limit: 10000 });
  const [paymentParams, setPaymentParams] = useState({ pagina: '', criterio: '5', limitePaginas: '' });
  const [purchaseParams, setPurchaseParams] = useState({ dateStart: '', dateEnd: '', supplier: '', number: '' });
  const [blingProductParams, setBlingProductParams] = useState({ number: '' });
  const [auvoParams, setAuvoParams] = useState({ since: '', dataInicial: '', dataFinal: '', customerId: '' });

  const operationSteps = useMemo(() => ({
    diagnostics: ['Consultando status do cec_api_integra.', 'Testando leituras principais e conexões configuradas.'],
    blingContacts: ['Chamando sincronização de contatos no Bling.', 'Atualizando cache do cec_api_integra.'],
    blingProducts: ['Chamando sincronização de produtos no Bling.', 'Atualizando cache do cec_api_integra.'],
    customers: ['Sincronizando clientes integrados no cec_api_integra.', 'Atualizando clientes locais.', 'Normalizando vínculos e dados do Cliente Mestre.'],
    products: ['Sincronizando produtos integrados.', 'Atualizando Produtos Mestres e vínculos.'],
    payments: ['Sincronizando formas de pagamento do Bling.', 'Atualizando opções usadas nas parcelas.'],
    purchases: ['Sincronizando pedidos de compra do Bling.', 'Atualizando a base usada pelos centros de custo.'],
    auvoCustomers: ['Chamando sincronização de clientes no Auvo.', 'Atualizando cache do cec_api_integra.'],
    auvoProducts: ['Chamando sincronização de produtos no Auvo.', 'Atualizando cache do cec_api_integra.'],
    auvoOrders: ['Chamando sincronização de OS no Auvo.', 'Atualizando cache do cec_api_integra.'],
    auvoTasks: ['Chamando sincronização de tarefas no Auvo.', 'Atualizando cache do cec_api_integra.'],
    auvoExpenses: ['Chamando sincronização de despesas no Auvo.', 'Atualizando cache do cec_api_integra.'],
    recommended: ['Bling: contatos, produtos, formas e compras.', 'Auvo: clientes, produtos e OS.', 'CEC Integra: consolidar clientes e produtos.', 'Task Hub: normalizar cadastros mestre.']
  }), []);

  const handleApiError = (requestError, fallback) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }
    setError(getErrorMessage(requestError, fallback));
  };

  const registerResult = (key, payload) => {
    setResults((current) => ({
      ...current,
      [key]: {
        status: 'success',
        at: new Date().toISOString(),
        summary: compactResult(payload),
        payload
      }
    }));
  };

  const registerFailure = (key, requestError, fallback) => {
    const message = getErrorMessage(requestError, fallback);
    setResults((current) => ({
      ...current,
      [key]: {
        status: 'error',
        at: new Date().toISOString(),
        summary: message,
        payload: requestError?.response?.data || null
      }
    }));
    return null;
  };

  const runDiagnostics = async ({ showStatus = true } = {}) => {
    if (showStatus) setActiveAction('diagnostics');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.cecDiagnostics();
      setDiagnostics(payload);
      registerResult('diagnostics', payload);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível consultar o diagnóstico da integração.');
      return registerFailure('diagnostics', requestError, 'Não foi possível consultar o diagnóstico da integração.');
    } finally {
      if (showStatus) setActiveAction('');
    }
    return null;
  };

  const syncBlingContacts = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('blingContacts');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncBlingContacts();
      registerResult('blingContacts', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar contatos do Bling.');
      return registerFailure('blingContacts', requestError, 'Não foi possível sincronizar contatos do Bling.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncBlingProducts = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('blingProducts');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncBlingProducts(blingProductParams);
      registerResult('blingProducts', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar produtos do Bling.');
      return registerFailure('blingProducts', requestError, 'Não foi possível sincronizar produtos do Bling.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncCustomers = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('customers');
    setError('');
    setNotice('');
    try {
      const sync = await CustomersAPI.syncCec({ limit: customerParams.limit });
      const normalized = await ClientConsolidationAPI.backfillAllNormalizedData({ limit: customerParams.normalizeLimit });
      const payload = { ...sync, normalized };
      registerResult('customers', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar e normalizar clientes.');
      return registerFailure('customers', requestError, 'Não foi possível sincronizar e normalizar clientes.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncProducts = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('products');
    setError('');
    setNotice('');
    try {
      const payload = await ProductConsolidationAPI.sync({ limit: productParams.limit });
      registerResult('products', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar produtos.');
      return registerFailure('products', requestError, 'Não foi possível sincronizar produtos.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncPayments = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('payments');
    setError('');
    setNotice('');
    try {
      const payload = await FormaPagamentoAPI.sincronizarFormasPagamento(paymentParams);
      registerResult('payments', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar formas de pagamento.');
      return registerFailure('payments', requestError, 'Não foi possível sincronizar formas de pagamento.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncPurchases = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('purchases');
    setError('');
    setNotice('');
    try {
      const payload = await ServiceCostCentersAPI.syncPurchaseOrders(purchaseParams);
      registerResult('purchases', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar pedidos de compra.');
      return registerFailure('purchases', requestError, 'Não foi possível sincronizar pedidos de compra.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncAuvoCustomers = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('auvoCustomers');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncAuvoCustomers({ since: auvoParams.since });
      registerResult('auvoCustomers', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar clientes do Auvo.');
      return registerFailure('auvoCustomers', requestError, 'Não foi possível sincronizar clientes do Auvo.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncAuvoProducts = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('auvoProducts');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncAuvoProducts({ since: auvoParams.since });
      registerResult('auvoProducts', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar produtos do Auvo.');
      return registerFailure('auvoProducts', requestError, 'Não foi possível sincronizar produtos do Auvo.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncAuvoOrders = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('auvoOrders');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncAuvoOrders();
      registerResult('auvoOrders', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar OS do Auvo.');
      return registerFailure('auvoOrders', requestError, 'Não foi possível sincronizar OS do Auvo.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncAuvoTasks = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('auvoTasks');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncAuvoTasks(auvoParams);
      registerResult('auvoTasks', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar tarefas do Auvo.');
      return registerFailure('auvoTasks', requestError, 'Não foi possível sincronizar tarefas do Auvo.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const syncAuvoExpenses = async ({ keepActive = false } = {}) => {
    if (!keepActive) setActiveAction('auvoExpenses');
    setError('');
    setNotice('');
    try {
      const payload = await IntegrationsAPI.syncAuvoExpenses({ since: auvoParams.since });
      registerResult('auvoExpenses', payload);
      return payload;
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível sincronizar despesas do Auvo.');
      return registerFailure('auvoExpenses', requestError, 'Não foi possível sincronizar despesas do Auvo.');
    } finally {
      if (!keepActive) setActiveAction('');
    }
  };

  const runRecommended = async () => {
    setActiveAction('recommended');
    setError('');
    setNotice('');
    try {
      const blingContacts = await syncBlingContacts({ keepActive: true });
      if (!blingContacts) return;
      const blingProducts = await syncBlingProducts({ keepActive: true });
      if (!blingProducts) return;
      const auvoCustomers = await syncAuvoCustomers({ keepActive: true });
      if (!auvoCustomers) return;
      const auvoProducts = await syncAuvoProducts({ keepActive: true });
      if (!auvoProducts) return;
      const auvoOrders = await syncAuvoOrders({ keepActive: true });
      if (!auvoOrders) return;
      const customers = await syncCustomers({ keepActive: true });
      if (!customers) return;
      const products = await syncProducts({ keepActive: true });
      if (!products) return;
      const payments = await syncPayments({ keepActive: true });
      if (!payments) return;
      const purchases = await syncPurchases({ keepActive: true });
      if (!purchases) return;
      setNotice('Sequência recomendada concluída. Gere a prévia da proposta novamente para validar cliente, produto e forma de pagamento.');
    } catch (_requestError) {
      setNotice('');
    } finally {
      setActiveAction('');
    }
  };

  const ShellTag = pageMode ? 'main' : 'section';
  const disabled = Boolean(activeAction);

  return (
    <ShellTag className={pageMode ? 'client-consolidation-page sync-center-page' : 'client-consolidation-panel sync-center-page'}>
      <div className="overlay-panel-header">
        <div>
          <div className="eyebrow">CEC Integra</div>
          <h2>Central de Sincronização</h2>
          <p className="text-muted mb-0">Execute as sincronizações administrativas em uma sequência única e previsível.</p>
        </div>
        {onClose && <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{pageMode ? 'Voltar' : 'Fechar'}</button>}
      </div>

      <div className="overlay-actions mt-3">
        <button type="button" className="btn btn-success" disabled={disabled} onClick={runRecommended}>Rodar sequência recomendada</button>
        <button type="button" className="btn btn-outline-primary" disabled={disabled} onClick={() => runDiagnostics({ showStatus: true })}>Verificar integração</button>
      </div>

      <OperationStatus
        active={Boolean(activeAction)}
        title={activeAction === 'recommended' ? 'Executando sequência de sincronização' : 'Sincronização em andamento'}
        steps={operationSteps[activeAction] || []}
      />

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      {notice && <div className="alert alert-success mt-3 mb-0">{notice}</div>}

      <div className="client-dashboard mt-4">
        <div className="client-total-card">
          <span>Status CEC</span>
          <strong>{diagnostics?.ok === false ? 'Atenção' : diagnostics?.ok ? 'OK' : '...'}</strong>
          <small>{diagnostics?.baseUrl || 'Consultando integração'}</small>
        </div>
        <div className="client-dashboard-section">
          <h3>Ordem recomendada</h3>
          <p><span>1. Bling</span><strong>Fonte</strong></p>
          <p><span>2. Auvo</span><strong>Fonte</strong></p>
          <p><span>3. CEC Integra</span><strong>Consolida</strong></p>
          <p><span>4. Task Hub</span><strong>Normaliza</strong></p>
        </div>
        <div className="client-dashboard-section">
          <h3>Últimos resultados</h3>
          {['blingContacts', 'auvoCustomers', 'customers', 'products'].map((key) => (
            <p key={key}><span>{({ blingContacts: 'Bling', auvoCustomers: 'Auvo', customers: 'Clientes CEC', products: 'Produtos CEC' })[key]}</span><strong>{results[key] ? formatDateTime(results[key].at) : 'Nunca'}</strong></p>
          ))}
        </div>
      </div>

      <section className="client-detail-section mt-4">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Aplicação</div>
            <h3>Bling</h3>
          </div>
          <span className="badge bg-light text-dark">Fonte comercial e financeira</span>
        </div>
        <div className="sync-action-grid mt-3">
          <article className="client-linked-card sync-action-card">
            <div><strong>Contatos</strong><span>Atualiza contatos do Bling usados como `contato.id` nas propostas comerciais.</span></div>
            <button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncBlingContacts()}>Sincronizar contatos</button>
          </article>
          <article className="client-linked-card sync-action-card">
            <div><strong>Produtos Bling</strong><span>Atualiza produtos brutos do Bling antes da consolidação de Produtos Mestres.</span></div>
            <div className="client-filter-bar mt-3">
              <div className="filter-control"><label className="form-label">Código específico</label><input className="form-control" value={blingProductParams.number} onChange={(event) => setBlingProductParams((current) => ({ ...current, number: event.target.value }))} placeholder="Opcional" /></div>
            </div>
            <button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncBlingProducts()}>Sincronizar produtos Bling</button>
          </article>
          <article className="client-linked-card sync-action-card">
            <div><strong>Formas de pagamento</strong><span>Atualiza IDs aceitos pelo Bling para as parcelas das propostas.</span></div>
            <div className="client-filter-bar mt-3">
              <div className="filter-control"><label className="form-label">Página</label><input className="form-control" type="number" value={paymentParams.pagina} onChange={(event) => setPaymentParams((current) => ({ ...current, pagina: event.target.value }))} placeholder="1" /></div>
              <div className="filter-control"><label className="form-label">Critério</label><input className="form-control" type="number" value={paymentParams.criterio} onChange={(event) => setPaymentParams((current) => ({ ...current, criterio: event.target.value }))} /></div>
              <div className="filter-control"><label className="form-label">Limite páginas</label><input className="form-control" type="number" value={paymentParams.limitePaginas} onChange={(event) => setPaymentParams((current) => ({ ...current, limitePaginas: event.target.value }))} placeholder="Opcional" /></div>
            </div>
            <button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncPayments()}>Sincronizar formas</button>
          </article>
          <article className="client-linked-card sync-action-card">
            <div><strong>Pedidos de compra</strong><span>Atualiza compras do Bling usadas para montar centros de custo.</span></div>
            <div className="client-filter-bar mt-3">
              <div className="filter-control"><label className="form-label">De</label><input className="form-control" type="date" value={purchaseParams.dateStart} onChange={(event) => setPurchaseParams((current) => ({ ...current, dateStart: event.target.value }))} /></div>
              <div className="filter-control"><label className="form-label">Até</label><input className="form-control" type="date" value={purchaseParams.dateEnd} onChange={(event) => setPurchaseParams((current) => ({ ...current, dateEnd: event.target.value }))} /></div>
              <div className="filter-control"><label className="form-label">Fornecedor ID</label><input className="form-control" value={purchaseParams.supplier} onChange={(event) => setPurchaseParams((current) => ({ ...current, supplier: event.target.value }))} placeholder="Opcional" /></div>
              <div className="filter-control"><label className="form-label">Número</label><input className="form-control" value={purchaseParams.number} onChange={(event) => setPurchaseParams((current) => ({ ...current, number: event.target.value }))} placeholder="Opcional" /></div>
            </div>
            <button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncPurchases()}>Sincronizar compras</button>
          </article>
        </div>
      </section>

      <section className="client-detail-section mt-4">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Aplicação</div>
            <h3>Auvo</h3>
          </div>
          <span className="badge bg-light text-dark">Fonte técnica e operacional</span>
        </div>
        <div className="client-filter-bar mt-3">
          <div className="filter-control"><label className="form-label">Desde</label><input className="form-control" type="date" value={auvoParams.since} onChange={(event) => setAuvoParams((current) => ({ ...current, since: event.target.value }))} /></div>
          <div className="filter-control"><label className="form-label">Tarefas de</label><input className="form-control" type="date" value={auvoParams.dataInicial} onChange={(event) => setAuvoParams((current) => ({ ...current, dataInicial: event.target.value }))} /></div>
          <div className="filter-control"><label className="form-label">Tarefas até</label><input className="form-control" type="date" value={auvoParams.dataFinal} onChange={(event) => setAuvoParams((current) => ({ ...current, dataFinal: event.target.value }))} /></div>
          <div className="filter-control"><label className="form-label">Cliente Auvo ID</label><input className="form-control" value={auvoParams.customerId} onChange={(event) => setAuvoParams((current) => ({ ...current, customerId: event.target.value }))} placeholder="Opcional" /></div>
        </div>
        <div className="sync-action-grid mt-3">
          <article className="client-linked-card sync-action-card"><div><strong>Clientes Auvo</strong><span>Atualiza clientes técnicos antes da consolidação de Clientes Mestres.</span></div><button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncAuvoCustomers()}>Sincronizar clientes Auvo</button></article>
          <article className="client-linked-card sync-action-card"><div><strong>Produtos Auvo</strong><span>Atualiza produtos técnicos antes da consolidação de Produtos Mestres.</span></div><button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncAuvoProducts()}>Sincronizar produtos Auvo</button></article>
          <article className="client-linked-card sync-action-card"><div><strong>Ordens de serviço</strong><span>Atualiza OS usadas em vínculos operacionais e relatórios.</span></div><button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncAuvoOrders()}>Sincronizar OS</button></article>
          <article className="client-linked-card sync-action-card"><div><strong>Tarefas Auvo</strong><span>Atualiza tarefas com filtros de período ou cliente.</span></div><button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncAuvoTasks()}>Sincronizar tarefas</button></article>
          <article className="client-linked-card sync-action-card"><div><strong>Despesas Auvo</strong><span>Atualiza despesas relacionadas a atendimentos e OS.</span></div><button type="button" className="btn btn-outline-primary mt-3" disabled={disabled} onClick={() => syncAuvoExpenses()}>Sincronizar despesas</button></article>
        </div>
      </section>

      <section className="client-detail-section mt-4">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Aplicação</div>
            <h3>CEC Integra e Task Hub</h3>
          </div>
          <span className="badge bg-light text-dark">Consolidação final</span>
        </div>
        <div className="sync-action-grid mt-3">
          <article className="client-linked-card sync-action-card">
            <div><strong>Clientes integrados</strong><span>Consolida Bling e Auvo no CEC Integra e normaliza Clientes Mestres do Task Hub.</span></div>
            <div className="client-filter-bar mt-3">
              <div className="filter-control"><label className="form-label">Limite leitura</label><input className="form-control" type="number" value={customerParams.limit} onChange={(event) => setCustomerParams((current) => ({ ...current, limit: event.target.value }))} /></div>
              <div className="filter-control"><label className="form-label">Lote normalização</label><input className="form-control" type="number" value={customerParams.normalizeLimit} onChange={(event) => setCustomerParams((current) => ({ ...current, normalizeLimit: event.target.value }))} /></div>
            </div>
            <button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncCustomers()}>Consolidar clientes</button>
          </article>
          <article className="client-linked-card sync-action-card">
            <div><strong>Produtos integrados</strong><span>Consolida Bling e Auvo no CEC Integra e atualiza Produtos Mestres do Task Hub.</span></div>
            <div className="client-filter-bar mt-3">
              <div className="filter-control"><label className="form-label">Limite leitura</label><input className="form-control" type="number" value={productParams.limit} onChange={(event) => setProductParams((current) => ({ ...current, limit: event.target.value }))} /></div>
            </div>
            <button type="button" className="btn btn-primary mt-3" disabled={disabled} onClick={() => syncProducts()}>Consolidar produtos</button>
          </article>
        </div>
      </section>

      <section className="client-detail-section mt-4">
        <h3>Histórico da sessão</h3>
        <div className="client-linked-list">
          {[
            'diagnostics',
            'blingContacts',
            'blingProducts',
            'payments',
            'purchases',
            'auvoCustomers',
            'auvoProducts',
            'auvoOrders',
            'auvoTasks',
            'auvoExpenses',
            'customers',
            'products'
          ].map((key) => {
            const row = results[key];
            const label = ({
              diagnostics: 'Diagnóstico',
              blingContacts: 'Bling contatos',
              blingProducts: 'Bling produtos',
              payments: 'Bling formas de pagamento',
              purchases: 'Bling pedidos de compra',
              auvoCustomers: 'Auvo clientes',
              auvoProducts: 'Auvo produtos',
              auvoOrders: 'Auvo OS',
              auvoTasks: 'Auvo tarefas',
              auvoExpenses: 'Auvo despesas',
              customers: 'CEC clientes integrados',
              products: 'CEC produtos integrados'
            })[key];
            return (
              <article key={key} className={`client-linked-card sync-result-card sync-result-card--${resultTone(row?.status)}`}>
                <div>
                  <strong>{label}</strong>
                  <span>{row ? `${formatDateTime(row.at)} · ${row.summary}` : 'Ainda não executado nesta sessão.'}</span>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </ShellTag>
  );
}
